import { Typography } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import List from '@material-ui/core/List';
import ListSubheader from '@material-ui/core/ListSubheader';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import WarningIcon from '@material-ui/icons/Warning';
import PropTypes from 'prop-types';
import React from 'react';
import { hotkeys } from 'react-keyboard-shortcuts';
import { BallBeat } from 'react-pure-loaders';
import DialogMultiActions from '../../core/DialogMultiActions';
import IRIentry from '../../core/IRIentry';
import LabelledSwitch from '../../core/LabelledSwitch';
import ListResult from '../../core/ListResult';
import PrimaryAddButton from '../../core/PrimaryAddButton';
import PrimaryRepeatButton from '../../core/PrimaryRepeatButton';
import ProjectLanguageSelector from '../../core/ProjectLanguageSelector';
import { globalStyles as styles } from '../../core/style/gstheme';
import TitledPanel from '../../core/TitledPanel';
import TitledTextField from '../../core/TitledTextField';
import { computeConceptIcon, isRTL, makeCancelable, sanitiseText, sortObjects } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import { MAX_LABEL_LENGTH } from '../constants';
import Errored from '../../core/Errored';
import SchemeLink from '../core/SchemeLink';
import CustomLanguage from '../../core/CustomLanguage';

class XLlabelDialog extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            open: false,
            acOpen: false,
            lang: this.props.defaultLang,
            literalForm: "",
            checking: false,
            processingIRI: false,
            checked: false,
            addedLabels: [],
            viewIRI: false,
            customLanguage: false,
            languageCode: "",
            languageCodeError: false,
            uniqueIRI: props.useCounterIRI ? true : false,
            resourceIRI: "",
            computedIRI: "",
            isErrored: null,
            iriRejected: false,
            isDuplicate: false
        };
        this.cancellable = null;
        this.textInput = React.createRef();
    }

    findXLlabel = (value) => {
        const { lang } = this.state;
        let searchParts = [
            {
                predicate: [
                    "http://www.w3.org/2004/02/skos/core#prefLabel",
                    "http://www.w3.org/2008/05/skos-xl#literalForm"
                ],
                value: value,
                isExactMatch: false
            }
        ];
        this.doAutocomplete(
            {
                graph: [this.props.graph],
                search: searchParts,
                language: lang,
                predicates: [
                    "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                    "https://grafsync.graphifi.com/schema/gs/status",
                    "http://www.w3.org/2004/02/skos/core#inScheme",
                    "http://www.w3.org/2004/02/skos/core#prefLabel",
                    "http://www.w3.org/2008/05/skos-xl#literalForm"
                ]
            })
            .promise
            .then((data) => {
                if (lang !== this.state.lang || (this.cancellable && this.cancellable.isCancelled())) return true;
                const mappedResults = data.results && data.results.length > 0 ? data.results.map(result => {
                    return {
                        label: result.prefLabel ? result.prefLabel : result.literalForm,
                        isXL: result.literalForm ? true : false,
                        status: result.status,
                        inScheme: result.inScheme,
                        type: result.type,
                        id: result.id
                    };
                }) : null;
                const sortedResults = mappedResults ? sortObjects([...mappedResults], 'label', lang).slice(0, 5) : null;
                this.setState({
                    results: sortedResults,
                    acOpen: data.results && data.results.length > 0 ? true : false
                });
                let isDuplicate = false;
                if (data.results && data.results.length > 0) {
                    isDuplicate = mappedResults.some(result =>
                        result.label[lang].toLowerCase() === this.state.literalForm.toLowerCase()
                    );
                }
                this.setState({ isDuplicate: isDuplicate, isUnusedLabel: null, checking: false, checked: true });
            })
            .catch((e) => {
                if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
            });
    };

    changeLanguage = (lang) => {
        let computedIRI = "";
        if (this.state.literalForm.length > 0) {
            const iriOptions = {
                prependText: this.props.iriOptions.xlOptions.prependText,
                appendText: this.props.iriOptions.xlOptions.shouldAppendText ? lang : "",
                textSeparator: "-"
            };
            computedIRI = GS.computeIRI(this.props.schemeIRI, this.state.literalForm, this.props.useSlashIRI, this.props.useUuidIRI, iriOptions);
        }
        this.setState({ lang, computedIRI }, () => this.checkValidity());
    };

    hot_keys = {
        'alt+n': {
            priority: 1,
            handler: () => this.handleClickOpen(),
        }
    };

    handleClickOpen = () => this.setState({ open: true, acOpen: false });

    handleClickAway = () => this.setState({ acOpen: false });

    // We keep a record of added labels to add to duplicate test because quickly added may not have reached DB
    updateAddedLabels = () => {
        this.setState((prevState) => {
            const ALs = [...prevState.addedLabels];
            ALs.push(prevState.literalForm);
            return { addedLabels: ALs };
        });
    };

    reserveIDs = async (resourceIRI) => {
        this.setState({ processingIRI: true });
        // Make IRI reservation call
        const isFound = await GS.reserveIRI(resourceIRI, this.props.graph, "http://www.w3.org/2008/05/skos-xl#Label");
        const exists = isFound.exists;
        if (exists) {
            this.rejectIRI(true);
            return false;
        }
        const reservationID = isFound.id;
        return {
            reservationID
        };
    };

    loadIDs(resourceIRI) {
        this.cancellable = makeCancelable(this.reserveIDs(resourceIRI));
        return this.cancellable;
    }

    getLabelCounter() {
        const { schemeIRI, graph, counters } = this.props;
        this.cancellable = makeCancelable(GS.getCounter(schemeIRI, graph, "secondary", counters.secondaryCounter));
        return this.cancellable;
    }

    getIDsByCounter = async () => {
        let ids;
        let resourceIRI;
        let tries = 0;
        while (!ids || ids === false) {
            tries++;
            if (tries > 20) {
                throw new Error("Cannot get unique IRI based on counter.");
            }
            const labelCountData = await this.getLabelCounter().promise;
            const labelCount = labelCountData.count;
            resourceIRI = GS.computeCounterIRI(this.props.schemeIRI, this.props.useSlashIRI, labelCount);
            ids = await this.loadIDs(resourceIRI).promise;
        }
        return {
            ids,
            resourceIRI
        };
    };

    addLabel = async () => {
        try {
            let resourceIRI = this.state.resourceIRI;
            let ids;
            if (this.props.useCounterIRI) {
                const counterInfo = await this.getIDsByCounter();
                ids = counterInfo.ids;
                resourceIRI = counterInfo.resourceIRI;
            }
            else {
                ids = await this.loadIDs(resourceIRI).promise;
            }
            if (this.cancellable && this.cancellable.isCancelled()) return true;
            if (ids !== false) {
                this.setState({ open: false, acOpen: false, resourceIRI }, async () => {
                    try {
                        GS.handle("add-label");
                        await this.processLabel(ids);
                    }
                    catch (e) {
                        if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
                    }
                });
            }
        }
        catch (e) {
            if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
        }
    };

    addLabelPlus = async () => {
        try {
            let resourceIRI = this.state.resourceIRI;
            let ids;
            if (this.props.useCounterIRI) {
                const labelCountData = await this.getLabelCounter().promise;
                const labelCount = labelCountData.count;
                resourceIRI = GS.computeCounterIRI(this.props.schemeIRI, this.props.useSlashIRI, labelCount);
            }
            ids = await this.loadIDs(resourceIRI).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return true;
            if (ids !== false) {
                this.setState({ resourceIRI }, async () => {
                    try {
                        GS.handle("add-label-plus");
                        await this.processLabel(ids);
                        this.textInput && this.textInput.current && this.textInput.current.focus();
                    }
                    catch (e) {
                        if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
                    }
                });
            }
        }
        catch (e) {
            if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
        }

    };

    processLabel = async (ids) => {
        // We need to make sure we have the parent loaded so narrower events happens
        const { reservationID } = ids;
        this.updateAddedLabels();
        const { literalForm, lang, resourceIRI } = this.state;
        this.setState({
            literalForm: "",
            acOpen: false,
            computedIRI: "",
            lang: this.props.defaultLang,
            resourceIRI: "",
            processingIRI: false,
            customLanguage: false,
            languageCode: "",
            languageCodeError: false,
            uniqueIRI: this.props.useCounterIRI ? true : false,
            viewIRI: false,
            iriRejected: false
        }, async () => {
            try {
                GS.handle("process-label");
                await this.props.onAddNewLabel(literalForm.trim(), lang, reservationID, resourceIRI);
            }
            catch (e) {
                if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
            }
        });
        this.textInput && this.textInput.current && this.textInput.current.focus();
    };

    exitAddLabel = () => {
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
        this.setState({
            open: false,
            literalForm: "",
            acOpen: false,
            lang: this.props.defaultLang,
            customLanguage: false,
            languageCode: "",
            languageCodeError: false,
            processingIRI: false,
            uniqueIRI: this.props.useCounterIRI ? true : false,
            iriRejected: false,
            viewIRI: false,
            computedIRI: "",
            resourceIRI: ""
        });
    };

    doAutocomplete(payload) {
        this.cancellable = makeCancelable(GS.autocomplete(payload));
        return this.cancellable;
    }

    insertExistingLabel = (labelIRI) => {
        this.props.onAddExistingLabel(labelIRI);
        this.exitAddLabel();
    };

    checkValidity = () => {
        if (this.state.literalForm.length > 0) {
            this.setState({ checking: true, checked: false }, () => {
                this.hcTimeout = setTimeout(() => {
                    if (this.state.addedLabels.includes(this.state.literalForm)) {
                        this.setState({ isDuplicate: true, checking: false, checked: true });
                        this.handleClickAway();
                    }
                    else this.findXLlabel(this.state.literalForm);
                }, 250);
            });
        }
        else {
            this.setState({ isDuplicate: false, checking: false, checked: true });
            this.handleClickAway();
        }
    };

    resetIRIconfirmation = () => this.setState({ uniqueIRI: false, iriRejected: false });

    confirmUniqueIRI = (iri) => this.setState({ uniqueIRI: true, resourceIRI: iri, iriRejected: false });

    rejectIRI = (processingIRI = false) => this.setState({ uniqueIRI: false, iriRejected: true, processingIRI });

    handleChange = (event) => {
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
        const t = event.target;
        const cleanText = sanitiseText(t.value.trimLeft());
        let computedIRI = this.state.computedIRI;
        if (!this.props.useCounterIRI && (cleanText.length > 0 && (!this.props.useUuidIRI || this.state.computedIRI === ""))) {
            const labelIriOptions = Object.assign({
                prependText: this.props.iriOptions.xlOptions.prependText,
                appendText: this.props.iriOptions.xlOptions.shouldAppendText ? this.props.defaultLang : "",
                textSeparator: "-"
            }, this.props.iriOptions);
            try {
                computedIRI = GS.computeIRI(this.props.schemeIRI, cleanText, this.props.useSlashIRI, this.props.useUuidIRI, labelIriOptions);
            }
            catch (e) {
                if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
            }
        }
        else {
            if (cleanText.length === 0) computedIRI = "";
        }
        this.setState({ literalForm: cleanText, computedIRI }, () => this.checkValidity());
    };

    handleCustomLanguageChange = checked => this.setState({
        customLanguage: checked,
        languageCode: "",
        lang: checked ? "" : this.props.defaultLang,
        literalForm: "",
        languageCodeError: false
    });

    handleLanguageCodeChange = (code, valid) => {
        this.setState({
            languageCode: code,
            lang: code,
            languageCodeError: !valid
        });
    };

    handleShowChange = checked => this.setState({ viewIRI: checked });

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
    }

    render() {

        const { literalForm, results, lang, isDuplicate, acOpen, checking, checked, processingIRI,
            addedLabels, computedIRI, uniqueIRI, iriRejected, isErrored, customLanguage, languageCode, languageCodeError } = this.state;
        const { classes, schemeIRI, defaultLang, graph, iriOptions, useWorkflow, useCounterIRI } = this.props;
        const invalid = literalForm.trim().length === 0 || literalForm.length > MAX_LABEL_LENGTH
            || checking || !checked || !uniqueIRI
            || languageCodeError
            || (customLanguage && languageCode === "");

        if (isErrored) return <Errored data={isErrored} dataTest="cl-error" />;

        const viewIRI = iriOptions.showIRI || this.state.viewIRI;
        const rtl = isRTL(lang);

        return (
            <React.Fragment>
                <Tooltip title="Add a SKOS-XL label (Alt+n)">
                    <IconButton aria-label="Add label" data-test="add-label" className={classes.smallIconButton} onClick={this.handleClickOpen}>
                        <AddCircleIcon />
                    </IconButton>
                </Tooltip>
                <Dialog
                    data-test="add-label-dialog"
                    maxWidth="md"
                    fullWidth
                    classes={{
                        paper: classes.paper,
                        paperWidthMd: classes.largeDialog
                    }}
                    open={this.state.open}
                    onClose={this.exitAddLabel} aria-labelledby="label-dialog-title">
                    <DialogTitle disableTypography className={classes.mainHeading} id="label-dialog-title">Add SKOS-XL Label</DialogTitle>
                    <DialogContent style={{
                        height:
                            viewIRI ?
                                '400px'
                                :
                                '290px'
                    }}>
                        <TitledPanel
                            dataTest="language-selection"
                            title="Language"
                            actions={
                                <LabelledSwitch
                                    dataTest="custom-language"
                                    margin={false}
                                    checked={customLanguage}
                                    handleChange={this.handleCustomLanguageChange}
                                    label="Custom" />
                            }>
                            {customLanguage ?
                                <CustomLanguage onCodeChange={this.handleLanguageCodeChange} />
                                :
                                <ProjectLanguageSelector includeLabel={false} onSelect={this.changeLanguage} />
                            }
                        </TitledPanel>
                        <form autoComplete="off" noValidate="novalidate">
                            <TitledTextField
                                dataTest="literal-form-text"
                                name="title"
                                title={"Literal Form"}
                                disabled={!lang || languageCodeError || (customLanguage && languageCode === "")}
                                autoFocus={true}
                                required={true}
                                margin={true}
                                inputRef={this.textInput}
                                placeholder="Enter literal form"
                                text={literalForm}
                                lang={lang}
                                adornment={
                                    checking ?
                                        <Tooltip title="Searching for results">
                                            <CircularProgress color="secondary" size={20} />
                                        </Tooltip>
                                        :
                                        iriRejected ?
                                            <InputAdornment position="end">
                                                <Tooltip title="There is an IRI clash">
                                                    <WarningIcon data-test="warning" />
                                                </Tooltip>
                                            </InputAdornment>
                                            :
                                            null
                                }
                                helperText={
                                    !acOpen && addedLabels.includes(literalForm.trim()) ?
                                        "You added this value recently."
                                        :
                                        null
                                }
                                maxLength={MAX_LABEL_LENGTH}
                                onEnter={!isDuplicate && checked ? this.addLabel : null}
                                onCtrlEnter={!isDuplicate && checked ? this.addLabelPlus : null}
                                onChange={this.handleChange} />
                            {acOpen &&
                                <ClickAwayListener onClickAway={this.handleClickAway}>
                                    <List
                                        data-test="list-results"
                                        dense
                                        className={classes.existingResults}
                                        subheader={<ListSubheader
                                            data-test="list-sub-header"
                                            style={{ ...(rtl) && { textAlign: 'right' } }}
                                            className={classes.existingResultsHeader}>Existing Labels</ListSubheader>}
                                        style={{ marginTop: -8 }}>
                                        {results && results.map((result, i) => {
                                            let label = result.label[lang];
                                            let additionalLabel = "";
                                            if (result.inScheme && !result.inScheme.includes(schemeIRI)) {
                                                additionalLabel = <div>
                                                    {result.inScheme.map(scheme =>
                                                        <SchemeLink handleNotAvailable={true} key={scheme} id={scheme} graph={graph} defaultLang={defaultLang} />
                                                    )}
                                                </div>;
                                            }
                                            else if (!result.inScheme) {
                                                additionalLabel = <div>Not in any scheme.</div>;
                                            }
                                            let finalAdditionalLabel = <div>
                                                {result.isXL ? <div>XL label</div> : <div>Concept preferred label</div>}
                                                {additionalLabel}
                                            </div>;
                                            const isInvalid = !result.type || result.type.includes("Concept") || (result.inScheme && result.inScheme.includes(schemeIRI));
                                            return <ListResult
                                                key={i}
                                                text={label}
                                                lang={lang}
                                                iri={result.id}
                                                pendingDeletion={false}
                                                invalid={isInvalid}
                                                entailed={result.isEntailed}
                                                listSecondIcon={computeConceptIcon(result, useWorkflow)}
                                                onClick={!isInvalid ? () => this.insertExistingLabel(result.id) : null}
                                                additionalLabel={finalAdditionalLabel} />;
                                        })}
                                    </List>
                                </ClickAwayListener>
                            }
                            {!iriOptions.showIRI && !useCounterIRI &&
                                <div style={{ textAlign: 'right' }}>
                                    <LabelledSwitch
                                        dataTest="show-iri"
                                        checked={viewIRI}
                                        disabled={literalForm === "" || processingIRI}
                                        handleChange={this.handleShowChange}
                                        label="View/Edit IRI" />
                                </div>
                            }
                            {!useCounterIRI && <IRIentry
                                key={computedIRI}
                                startIRI={computedIRI}
                                disabled={literalForm === "" || processingIRI}
                                visible={viewIRI}
                                graph={graph}
                                componentIRI={schemeIRI}
                                handleIRIclash={iriOptions.handleIRIclash}
                                resetIRIconfirmation={this.resetIRIconfirmation}
                                confirmUniqueIRI={this.confirmUniqueIRI}
                                prependText="label"
                                appendText="lang"
                                textSeparator="-"
                                changeIRI={() => { }}
                            />
                            }
                        </form>
                    </DialogContent>
                    {processingIRI ?
                        <DialogActions className={classes.dialogActions}>
                            <div style={{ display: 'flex', height: 40 }}>
                                <Typography className={classes.subMainHading}>Processing ...</Typography>
                                <span style={{ marginLeft: 24 }}>
                                    <BallBeat color='#97d700' loading={true} />
                                </span>
                            </div>
                        </DialogActions>
                        :
                        <DialogMultiActions onCancel={this.exitAddLabel}>
                            <PrimaryAddButton title="Add a label" disabled={invalid} onClick={this.addLabel} />
                            <PrimaryRepeatButton
                                dataTest="add-multiple-labels"
                                title="Add label and create another (Ctrl+Enter)"
                                disabled={invalid}
                                onClick={this.addLabelPlus} />
                        </DialogMultiActions>
                    }
                </Dialog>
            </React.Fragment>
        );
    }
}

XLlabelDialog.propTypes = {
    graph: PropTypes.string.isRequired,
    classes: PropTypes.object,
    projectIRI: PropTypes.string.isRequired,
    defaultLang: PropTypes.string.isRequired,
    onAddExistingLabel: PropTypes.func.isRequired,
    onAddNewLabel: PropTypes.func.isRequired,
    schemeIRI: PropTypes.string.isRequired,
    useWorkflow: PropTypes.bool,
    useSlashIRI: PropTypes.bool,
    useCounterIRI: PropTypes.bool,
    counters: PropTypes.object,
    useUuidIRI: PropTypes.bool,
    iriOptions: PropTypes.object
};

export default withStyles(styles)(hotkeys(XLlabelDialog));